/**
 * DocumentInfo · P17-UAT-09 REV-A (Jun 2026)
 *
 * Standard "DOCUMENT INFORMATION" audit block for transactional-document
 * View/detail screens. Presentation-only — renders creation/audit metadata
 * from authoritative existing fields (`created_at`, `updated_at`,
 * `created_by`). It NEVER fabricates a creator: when `createdBy` is absent
 * it shows the "—" placeholder.
 *
 * Reference design: P17-UAT-07 CWO Document Information (ChildWorkOrders.jsx).
 * Uses the shared IST formatters so every audit surface reads identically:
 *   Created Date  → DD-MM-YYYY
 *   Created Time  → hh:mm:ss AM/PM
 *   Last Updated  → DD-MM-YYYY hh:mm:ss AM/PM
 *
 * This is SYSTEM audit metadata, kept strictly separate from BUSINESS dates
 * (Order Date, Pack Date, Challan Date, Invoice Date, PI/EPL date, …) and
 * from lifecycle metadata (approved_by, cancelled_by, …).
 */
import React from "react";
import { fmtDateIST, fmtTime12IST, fmtDateTime12IST } from "@/lib/datetime";

function Field({ label, value, testid, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div data-testid={testid} className={`font-mono text-xs ${accent ? "text-[var(--accent)]" : "text-white"}`}>{value || "—"}</div>
    </div>
  );
}

export function DocumentInfo({
  docNo,
  createdAt,
  updatedAt,
  createdBy,
  docLabel = "Document No.",
  testidPrefix = "doc",
  className = "mb-4",
}) {
  return (
    <div className={`border border-[var(--border)] rounded-sm p-3 ${className}`} data-testid={`${testidPrefix}-doc-info`}>
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Document Information</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label={docLabel} value={docNo} accent testid={`${testidPrefix}-doc-no`} />
        <Field label="Created Date" value={fmtDateIST(createdAt)} testid={`${testidPrefix}-created-date`} />
        <Field label="Created Time" value={fmtTime12IST(createdAt)} testid={`${testidPrefix}-created-time`} />
        <Field label="Created By" value={createdBy || "—"} testid={`${testidPrefix}-created-by`} />
        <Field label="Last Updated" value={fmtDateTime12IST(updatedAt)} testid={`${testidPrefix}-last-updated`} />
      </div>
    </div>
  );
}

export default DocumentInfo;
